import { CurecallModalContainer, CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorQueryHandler } from '../error/error-query-handler.component'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { PatientService } from 'src/services/patient.service'
import { createContext, useContext, useRef, useCallback, useState } from 'react'
import MedEventForm, { TPatientMedEventForForm } from './med-event-form.component'
import { useNotification } from 'src/providers/notification.provider'

type TMedEventModalContext = {
  setHasFormBeenModified: (value: boolean) => void
  handleOnDelete?: () => void
}

export const MedEventModalContext = createContext<TMedEventModalContext>({
  setHasFormBeenModified: () => {},
})
export const useMedEventModalContext = () => useContext(MedEventModalContext)

type TMedEventModalProps = {
  patientId: number
  patientMedEventForForm: TPatientMedEventForForm
  onToggle: (event?: React.MouseEvent<HTMLElement>) => void
}

const MedEventModal = ({ patientId, patientMedEventForForm, onToggle }: TMedEventModalProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const { project, getMedEventLabelById } = useCurrentProject()
  const queryClient = useQueryClient()

  const hasFormBeenModifiedRef = useRef<boolean>(false)
  const isDeletingPatientMedEvent = useRef<boolean>(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)

  const {
    isLoading: isLoadingPatient,
    error: errorPatient,
    data: patient,
  } = useQuery({
    queryKey: ['patients', project.id, patientId],
    queryFn: () => PatientService.getById(project.id, patientId),
    staleTime: 60 * 2000, // won't fetch patient again before 2 minutes
  })

  const setHasFormBeenModified = useCallback((value: boolean) => {
    hasFormBeenModifiedRef.current = value
  }, [])

  const handleOnToggle = (event?: React.MouseEvent<HTMLElement>, isSubmittingForm?: boolean) => {
    if (hasFormBeenModifiedRef.current && !isSubmittingForm) {
      setShowWarningModal(true)
      return
    }
    // when clicking on confirm delete modal do not close the med event modal yet
    if (isDeletingPatientMedEvent.current) {
      return
    }
    onToggle(event)
    setShowWarningModal(false)
    hasFormBeenModifiedRef.current = false
  }

  const handleOnDelete = () => {
    setShowConfirmDeleteModal(true)
    isDeletingPatientMedEvent.current = true
  }
  const handleConfirmDelete = async () => {
    try {
      await PatientService.deletePatientMedEvent(project.id, patientId, patientMedEventForForm.id)
      notify({
        title: t('components.med-events-form.delete-success-title'),
        message: t('components.med-events-form.delete-success-message'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
      queryClient
        .getQueriesData({ queryKey: ['planning-patient-med-events'] })
        .forEach(async ([queryKey, queryData]) => {
          const planningPatientMedEvents = (queryData as any)?.patientMedEvents || []
          const hasAPatientMedEventOfListBeenUpdated = planningPatientMedEvents.some(
            (planningPatientMedEvent: { id: number }) =>
              patientMedEventForForm.id === planningPatientMedEvent.id,
          )

          if (hasAPatientMedEventOfListBeenUpdated) {
            await queryClient.invalidateQueries({ queryKey })
          }
        })
      isDeletingPatientMedEvent.current = false
      setShowConfirmDeleteModal(false)
      onToggle()
    } catch (e) {
      notify({ title: t('components.med-events-form.delete-error-title'), message: e, appearance: 'ERROR' })
      isDeletingPatientMedEvent.current = false
      setShowConfirmDeleteModal(false)
    }
  }
  const handleCancelDelete = () => {
    setShowConfirmDeleteModal(false)
    isDeletingPatientMedEvent.current = false
  }

  const handleConfirmClose = () => {
    setShowWarningModal(false)
    hasFormBeenModifiedRef.current = false
    onToggle()
  }
  const handleCancelClose = () => {
    setShowWarningModal(false)
  }

  if (errorPatient) {
    return <ErrorQueryHandler error={errorPatient} />
  }

  if (isLoadingPatient || !patient) {
    return <div className="loading" />
  }

  if (!patientMedEventForForm) {
    return <ErrorQueryHandler error={errorPatient} />
  }

  return (
    <>
      <CurecallModalContainer
        closeModalAriaLabel={t('common.actions.close')}
        title={t('components.med-events-form.title', {
          medEventName: getMedEventLabelById(patientMedEventForForm?.MedEvent?.id),
        })}
        isOpen={true}
        toggle={handleOnToggle}
        dialogClassName="c-w-[72vw] c-max-w-[1300px]"
      >
        <MedEventModalContext.Provider value={{ setHasFormBeenModified }}>
          <MedEventForm
            patient={patient}
            patientMedEventForForm={patientMedEventForForm}
            onToggle={handleOnToggle}
            onDelete={handleOnDelete}
          />
        </MedEventModalContext.Provider>
      </CurecallModalContainer>

      {showWarningModal && (
        <CurecallConfirmationModal
          isOpen={showWarningModal}
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
          title={t('components.generic-followups-form.closing-title')}
          confirmButtonLabel={t('components.med-events-form.confirm-close')}
          cancelButtonLabel={t('components.med-events-form.cancel-close')}
          color="ALERT"
        >
          {t('components.generic-followups-form.warning-closing-without-saving')}
        </CurecallConfirmationModal>
      )}

      {showConfirmDeleteModal && (
        <CurecallConfirmationModal
          isOpen={showConfirmDeleteModal}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={t('components.med-events-form.deleting-title')}
          confirmButtonLabel={t('components.med-events-form.confirm-delete')}
          cancelButtonLabel={t('components.med-events-form.cancel-delete')}
          color="ALERT"
        >
          {t('components.med-events-form.warning-deleting', {
            medEventName: getMedEventLabelById(patientMedEventForForm?.MedEvent?.id),
          })}
        </CurecallConfirmationModal>
      )}
    </>
  )
}

export default MedEventModal
